"use client";
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Role } from '@/types/roles';
import { useSignIn } from '@clerk/nextjs';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Loader2, Mail, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import Image from 'next/image';

interface StudentTrackLoginFormProps {
  track: string; // np | ap | img
  roleVariant?: Role; // optional more specific role enum
}

const DEFAULT_POST_LOGIN = '/student/profile';

// Resolve where to send the user after login. Honors the `redirect_url` query
// param (set by the auth middleware) so deep links like /student/bookings/:id
// survive the login bounce. Only same-origin targets are allowed to avoid
// open-redirects; otherwise falls back to the student dashboard.
function resolvePostLoginPath(): string {
  if (typeof window === 'undefined') return DEFAULT_POST_LOGIN;
  const param = new URLSearchParams(window.location.search).get('redirect_url');
  if (!param) return DEFAULT_POST_LOGIN;
  try {
    const url = new URL(param, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // ignore malformed redirect_url
  }
  return DEFAULT_POST_LOGIN;
}

export function StudentTrackLoginForm({ track }: StudentTrackLoginFormProps) {
  const { signIn, setActive, isLoaded } = useSignIn();
  const router = useRouter();
  // IMG students land in their dedicated portal; NP/PA keep the existing destination.
  const successRedirect = track === 'img' ? '/student/img/applications' : '/student/profile';
  const [isLoading, setIsLoading] = React.useState<'google' | 'apple' | 'email' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [captchaVisible, setCaptchaVisible] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Watch for CAPTCHA content
  React.useEffect(() => {
    if (!mounted) return;
    const checkCaptcha = () => {
      const el = document.getElementById('clerk-captcha');
      if (el && el.children.length > 0) {
        setCaptchaVisible(true);
      }
    };
    checkCaptcha();
    const observer = new MutationObserver(checkCaptcha);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [mounted]);

  const handleOAuth = async (provider: 'google' | 'apple') => {
    if (!isLoaded || !signIn) {
      setError('Sign-in is still loading. Please try again.');
      return;
    }

    setIsLoading(provider);
    setError(null);

    try {
      const strategy = provider === 'google' ? 'oauth_google' : 'oauth_apple';
      
      const result = await signIn.create({
        strategy,
        redirectUrl: `${window.location.origin}/sso-callback`,
        actionCompleteRedirectUrl: `${window.location.origin}${resolvePostLoginPath()}`,
      });

      const externalUrl = result.firstFactorVerification?.externalVerificationRedirectURL;
      if (externalUrl) {
        window.location.href = externalUrl.toString();
      } else {
        setError('Could not start sign-in. Please try again.');
        setIsLoading(null);
      }
    } catch (err: unknown) {
      const clerkErrors = (err as { errors?: Array<{ message?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        setError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
      } else {
        setError(`Failed to sign in with ${provider === 'google' ? 'Google' : 'Apple'}. Please try again.`);
      }
      setIsLoading(null);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) {
      setError('Sign-in is still loading. Please try again.');
      return;
    }

    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setIsLoading('email');
    setError(null);

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        window.location.href = resolvePostLoginPath();
      } else {
        setError('Sign-in incomplete. Please try again or use Google/Apple sign-in.');
      }
    } catch (err: unknown) {
      const clerkErrors = (err as { errors?: Array<{ message?: string; code?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        const invalidCreds = clerkErrors.some((e) => 
          e.code === 'form_password_incorrect' || 
          e.code === 'form_identifier_not_found'
        );
        if (invalidCreds) {
          setError('Invalid email or password.');
        } else {
          setError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
        }
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setIsLoading(null);
    }
  };

  const trackCopy: Record<string, { heading: string; subtitle: string }> = {
    np: {
      heading: 'Nurse Practitioner',
      subtitle: 'Access your clinical rotation dashboard',
    },
    ap: {
      heading: 'Physician Assistant',
      subtitle: 'Track your competency progression',
    },
    img: {
      heading: 'IMG Track',
      subtitle: 'Navigate your US clinical journey',
    },
  };
  const c = trackCopy[track] || trackCopy.np;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Back button */}
      <div className={`p-6 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <button
          onClick={() => router.push('/')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
        >
          <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to home</span>
        </button>
      </div>

      {/* Form Container */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className={`w-full max-w-md transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Logo */}
          <div className="text-center mb-8">
            <Link href="/">
              <Image
                src="/images/Ace-Main-Logo.png"
                alt="American Clinical Experience"
                width={240}
                height={96}
                className="h-24 w-auto object-contain mx-auto"
                priority
              />
            </Link>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-4">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                Student Portal
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{c.heading}</h2>
              <p className="text-gray-500 text-sm">{c.subtitle}</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 animate-in slide-in-from-top-2 duration-300">
                {error}
              </div>
            )}

            {/* OAuth Buttons */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                type="button"
                onClick={() => handleOAuth('google')}
                disabled={isLoading !== null}
                className="h-12 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading === 'google' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                      <path fill="#4285F4" d="M14.9 8.161c0-.476-.039-.954-.121-1.422h-6.64v2.695h3.802a3.24 3.24 0 01-1.407 2.127v1.75h2.269c1.332-1.22 2.097-3.02 2.097-5.15z" />
                      <path fill="#34A853" d="M8.14 15c1.898 0 3.499-.62 4.665-1.69l-2.268-1.749c-.631.427-1.446.669-2.395.669-1.836 0-3.393-1.232-3.952-2.888H1.85v1.803A7.044 7.044 0 008.14 15z" />
                      <path fill="#FBBC04" d="M4.187 9.342a4.17 4.17 0 010-2.68V4.859H1.849a6.97 6.97 0 000 6.286l2.338-1.803z" />
                      <path fill="#EA4335" d="M8.14 3.77a3.837 3.837 0 012.7 1.05l2.01-1.999a6.786 6.786 0 00-4.71-1.82 7.042 7.042 0 00-6.29 3.858L4.186 6.66c.556-1.658 2.116-2.89 3.952-2.89z" />
                    </svg>
                    Google
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => handleOAuth('apple')}
                disabled={isLoading !== null}
                className="h-12 rounded-xl bg-black text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-800 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading === 'apple' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16.365 1.43c0 1.14-.42 2.07-1.252 2.893-.83.814-1.767 1.29-2.807 1.218-.055-1.09.38-2.08 1.19-2.917.81-.84 1.856-1.32 2.87-1.38.002.062.002.124.002.186zM21.596 17.44c-.62 1.44-.99 2.18-1.85 3.52-1.2 1.86-2.89 4.17-5.01 4.19-1.17.012-1.47-.76-3.06-.76-1.59 0-1.93.74-3.14.772-2.07.06-3.65-2.12-4.85-3.97-3.34-5.11-3.7-11.1-1.64-14.29 1.46-2.26 3.77-3.58 5.94-3.58 2.22 0 3.62.76 5.45.76 1.78 0 2.89-.76 5.06-.76.88 0 3.22.32 4.73 2.42-4.07 2.22-3.42 7.99.06 10.45-.84 1.64-1.83 2.76-1.66 2.99z" />
                    </svg>
                    Apple
                  </>
                )}
              </button>
            </div>

            {/* CAPTCHA container - always in DOM */}
            <div id="clerk-captcha" className={captchaVisible ? "hidden" : "flex justify-center"} />
            
            {/* CAPTCHA modal - rendered via portal when visible */}
            {mounted && captchaVisible && ReactDOM.createPortal(
              <div className="fixed inset-0 z-[9999]">
                <div className="absolute inset-0 bg-black/50" />
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                    <div className="flex flex-col items-center text-center mb-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-4">
                        <ShieldCheck className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Security Check</h3>
                      <p className="text-sm text-gray-500">Please complete the verification below</p>
                    </div>
                    <div className="flex justify-center" ref={(el) => {
                      if (el) {
                        const captcha = document.getElementById('clerk-captcha');
                        if (captcha && captcha.children.length > 0) {
                          el.innerHTML = '';
                          while (captcha.firstChild) {
                            el.appendChild(captcha.firstChild);
                          }
                        }
                      }
                    }} />
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">or continue with email</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSignIn} className="space-y-4">
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                />
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                  placeholder="Password"
                  className="w-full h-12 pl-12 pr-12 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <button
                type="submit"
                disabled={isLoading !== null || !email || !password}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                {isLoading === 'email' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {/* Sign up link */}
            <div className="mt-6 text-center text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <Link 
                href={`/student/${track}/signup`} 
                className="text-blue-600 font-semibold hover:text-blue-700 transition-colors"
              >
                Create account
              </Link>
            </div>
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-gray-400">
            © 2024 American Clinical Experience
          </p>
        </div>
      </div>
    </div>
  );
}
export default StudentTrackLoginForm;
